import React, { useState, useEffect, useRef, useMemo } from 'react';
import { generatePartialPairAssignments, generateGroupPairs } from './utils/pairGeneration';
import { BarChart3, PlayCircle, TrendingUp, Users, Settings, AlertCircle, UserX, LogIn, LogOut, FileText, Menu, X } from 'lucide-react';
import { useMsal, useIsAuthenticated, AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { EventType } from '@azure/msal-browser';
import { loginRequest } from './authConfig';
import { api, resolvePhotoUrl } from './services/api';
import ComparisonView from './components/ComparisonView';
import RankingsView from './components/RankingsView';
import OverviewTab from './components/OverviewTab';
import RoundsTab from './components/RoundsTab';
import RankingsTab from './components/RankingsTab';
import EmployeesTab from './components/EmployeesTab';
import SettingsTab from './components/SettingsTab';
import SkippedComparisonsTab from './components/SkippedComparisonsTab';
import AuditLogTab from './components/AuditLogTab';
import NewRoundModal from './components/NewRoundModal';
import type {
  Employee,
  Round,
  Ranking,
  ComparisonWithQuestions,
  Comparison,
  UnknownEmployee,
  TabType
} from './types';

type ViewType = 'comparison' | 'dashboard';

const App: React.FC = () => {
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = instance.getActiveAccount();

  // Current user from /api/auth/me (Employees table)
  const [currentUser, setCurrentUser] = useState<Employee | null>(null);
  const currentUserId = currentUser?.employeeID || 0;
  const userRole = currentUser?.role || 'user';
  const userName = currentUser?.name || account?.name || '';
  const userEmail = currentUser?.email || account?.username || '';

  const [currentView, setCurrentView] = useState<ViewType>('dashboard');
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  // Data state
  const [rounds, setRounds] = useState<Round[]>([]);
  const [assignedRoundIds, setAssignedRoundIds] = useState<number[] | null>(null);
  const [activeRound, setActiveRound] = useState<Round | null>(null);
  const [lastWorkingRoundId, setLastWorkingRoundId] = useState<number | null>(null);
  const [rankings, setRankings] = useState<Ranking[]>([]);
  const [rankingsRoundId, setRankingsRoundId] = useState<number | null>(null);
  const [unknownEmployees, setUnknownEmployees] = useState<UnknownEmployee[]>([]);
  const [loading, setLoading] = useState(false);
  const [userLoading, setUserLoading] = useState(true);
  const [userLoadError, setUserLoadError] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notification, setNotification] = useState<{ message: string; type: 'info' | 'warning' | 'error' } | null>(null);

  // Auto-dismiss notification after 5 seconds
  useEffect(() => {
    if (!notification) return;
    const timer = setTimeout(() => setNotification(null), 5000);
    return () => clearTimeout(timer);
  }, [notification]);

  // Comparison state
  const [currentComparison, setCurrentComparison] = useState<ComparisonWithQuestions | null>(null);
  const [userComparisons, setUserComparisons] = useState<Comparison[]>([]);
  const [showRankings, setShowRankings] = useState(false);
  const [loadingComparison, setLoadingComparison] = useState(false);
  const [lastSubmittedComparisonId, setLastSubmittedComparisonId] = useState<number | null>(null);
  const [lastSkippedComparisonId, setLastSkippedComparisonId] = useState<number | null>(null);

  // Modal state
  const [showNewRoundModal, setShowNewRoundModal] = useState(false);

  // Mobile navigation menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // System settings state
  const [eloKFactor, setEloKFactor] = useState(21);
  const [initialRating, setInitialRating] = useState(1500);

  // Browser history refs — prevent the SPA from losing navigation state on back/forward
  const hasInitialized = useRef(false);
  const isPopStateNavigation = useRef(false);

  // Seed history once after authentication so there's always an entry to fall back to
  useEffect(() => {
    if (isAuthenticated && !hasInitialized.current) {
      hasInitialized.current = true;
      window.history.replaceState({ view: currentView, tab: activeTab }, '');
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Push a new history entry on every navigation (skip when the navigation itself came from popstate)
  useEffect(() => {
    if (!hasInitialized.current || isPopStateNavigation.current) return;
    window.history.pushState({ view: currentView, tab: activeTab }, '');
  }, [currentView, activeTab]);

  // Restore app state when the user presses Back / Forward
  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      isPopStateNavigation.current = true;
      const state = e.state as { view?: string; tab?: string } | null;
      if (state?.view === 'comparison') {
        setCurrentView('comparison');
      } else if (state?.view === 'dashboard') {
        setCurrentView('dashboard');
        setActiveTab((state.tab as TabType) || 'overview');
        setShowRankings(false);
      } else {
        // No recognisable state — return to the default screen
        setCurrentView('dashboard');
        setActiveTab('overview');
        setShowRankings(false);
        window.history.pushState({ view: 'dashboard', tab: 'overview' }, '');
      }
      setTimeout(() => { isPopStateNavigation.current = false; }, 0);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Fire exactly once after the user completes an interactive sign-in redirect.
  // LOGIN_SUCCESS does NOT fire on silent token refresh or page reload.
  useEffect(() => {
    const callbackId = instance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS) {
        api.logLogin().catch(err => console.error('Failed to log login event:', err));
      }
    });
    return () => { if (callbackId) instance.removeEventCallback(callbackId); };
  }, [instance]);

  // Load current user and initial data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadCurrentUser();
      loadInitialData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const loadCurrentUser = async () => {
    setUserLoading(true);
    setUserLoadError(false);
    try {
      const user = await api.getCurrentUser();
      setCurrentUser(user);
    } catch (err) {
      // Don't show "Account Not Registered" for auth/network failures (e.g. expired token triggering
      // a login redirect). Only show it when we have a confirmed user record missing in the DB.
      setUserLoadError(true);
      console.error('Error loading current user:', err);
    } finally {
      setUserLoading(false);
    }
  };

  const refreshAssignedRoundIds = async () => {
    if (currentUser && currentUser.employeeID > 0) {
      try {
        const ids = await api.getAssignedRoundIds(currentUser.employeeID);
        setAssignedRoundIds(ids);
      } catch (err) {
        console.error('Error loading assigned rounds:', err);
      }
    }
  };

  // Load assigned round IDs for all users (gates who can perform comparisons)
  useEffect(() => {
    refreshAssignedRoundIds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Refresh rounds on every tab switch so stale data is never shown.
  // assignedRoundIds only refreshed when entering the rounds tab — they only change
  // when another admin modifies evaluator assignments, so checking per-tab-switch is wasteful.
  useEffect(() => {
    if (!isAuthenticated || !currentUser) return;
    refreshRounds();
    if (activeTab === 'rounds') refreshAssignedRoundIds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // For non-admin users, filter rounds to only assigned ones
  const visibleRounds = userRole === 'admin' || !assignedRoundIds
    ? rounds
    : rounds.filter(r => assignedRoundIds.includes(r.roundID));

  // For Rankings and Skipped tabs: admins see only rounds they created or are assigned to
  const myRounds = useMemo(() => {
    if (userRole !== 'admin') return visibleRounds;
    return rounds.filter(r =>
      r.createdBy === userEmail || assignedRoundIds?.includes(r.roundID)
    );
  }, [rounds, userRole, userEmail, assignedRoundIds, visibleRounds]);

  // Reset undo state when the active round changes so the Undo button never
  // bleeds over from a previous round into a freshly started one.
  useEffect(() => {
    setLastSubmittedComparisonId(null);
    setLastSkippedComparisonId(null);
  }, [activeRound?.roundID]);

  useEffect(() => {
    if (activeRound && currentView === 'comparison' && !showRankings) {
      loadNextPair();
      loadUserComparisons();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRound, currentView, showRankings]);

  useEffect(() => {
    if (activeRound) {
      loadUnknownEmployees();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRound]);

  const loadInitialData = async () => {
    setLoading(true);
    setError(null);
    try {
      const roundsData = await api.getRounds();

      setRounds(roundsData);

      const active = roundsData.find(r => r.status === 'active');
      if (active) {
        setActiveRound(active);
        setLastWorkingRoundId(active.roundID);
        await loadRankings();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      console.error('Error loading initial data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadRankings = async (roundId?: number) => {
    try {
      const id = roundId ?? activeRound?.roundID;
      if (id) {
        // For grouped rounds, pass evaluator ID to show only the evaluator's group employees
        const round = rounds.find(r => r.roundID === id);
        const evalId = round?.isGrouped ? currentUserId : undefined;
        const rankingsData = await api.getRankings(id, evalId);
        setRankings(rankingsData);
        setRankingsRoundId(id);
      } else {
        // Fallback to aggregate rankings
        const rankingsData = await api.getAggregateRankings();
        setRankings(rankingsData);
        setRankingsRoundId(null);
      }
    } catch (err) {
      console.error('Error loading rankings:', err);
    }
  };

  const loadNextPair = async () => {
    if (!activeRound) return;

    setLoadingComparison(true);
    try {
      const comparison = await api.getNextPair(currentUserId, activeRound.roundID);
      if (comparison) {
        setCurrentComparison(comparison);
      } else {
        // No more pairs for this evaluator — show rankings.
        // Only mark the round complete if ALL evaluators across ALL assigned pairs are done.
        setCurrentComparison(null);
        setShowRankings(true);
        await loadRankings(activeRound.roundID);
        try {
          await api.tryCompleteRound(activeRound.roundID);
          const roundsData = await api.getRounds();
          setRounds(roundsData);
          // Find next active round that the user is assigned to
          const nextActiveRound = roundsData.find(r => r.status === 'active' && assignedRoundIds?.includes(r.roundID));
          setActiveRound(nextActiveRound || null);
        } catch (statusErr) {
          console.error('Error checking round completion:', statusErr);
        }
      }
    } catch (err: any) {
      console.error('Error loading next pair:', err);
      // Round was deleted by the creator — find next active round or go to dashboard
      if (err?.deleted) {
        setCurrentComparison(null);
        setCurrentView('dashboard');
        setActiveTab('rounds');
        try {
          const roundsData = await api.getRounds();
          setRounds(roundsData);
          const nextActiveRound = roundsData.find(r => r.status === 'active' && assignedRoundIds?.includes(r.roundID));
          setActiveRound(nextActiveRound || null);
        } catch {
          setActiveRound(null);
        }
        setNotification({ message: 'This round has been deleted by the creator.', type: 'warning' });
        return;
      }
      // No pair available — show rankings and mark round completed
      setCurrentComparison(null);
      setShowRankings(true);
      await loadRankings(activeRound.roundID);
      try {
        await api.tryCompleteRound(activeRound.roundID);
        const roundsData = await api.getRounds();
        setRounds(roundsData);
        // Find next active round that the user is assigned to
        const nextActiveRound = roundsData.find(r => r.status === 'active' && assignedRoundIds?.includes(r.roundID));
        setActiveRound(nextActiveRound || null);
      } catch {
        // ignore — completion check is best-effort
      }
    } finally {
      setLoadingComparison(false);
    }
  };

  const loadUserComparisons = async () => {
    if (!activeRound) return;

    try {
      const comparisons = await api.getUserComparisons(currentUserId, activeRound.roundID);
      setUserComparisons(comparisons);
    } catch (err) {
      console.error('Error loading user comparisons:', err);
    }
  };

  const loadUnknownEmployees = async () => {
    if (!activeRound) return;

    try {
      const unknowns = await api.getUnknownEmployees(activeRound.roundID);
      setUnknownEmployees(unknowns);
    } catch (err) {
      console.error('Error loading unknown employees:', err);
    }
  };

  const handleCreateRound = async ({ roundName, questions, selectedEmployeeIDs, selectedEvaluators, comparisonMode, accuracy, isGrouped, finalReviewerId, evaluatorGroupMappings, hasSecondaryEvaluators, secondaryEvaluatorMappings, frSelectionCount }: { roundName: string; questions: { text: string }[]; selectedEmployeeIDs?: number[]; selectedEvaluators?: { id: number; name: string }[]; comparisonMode: 'full' | 'partial'; accuracy?: number; isGrouped?: boolean; finalReviewerId?: number; evaluatorGroupMappings?: Array<{ evaluatorId: number; employeeIds: number[] }>; hasSecondaryEvaluators?: boolean; secondaryEvaluatorMappings?: { primaryEvaluatorId: number; secondaryEvaluatorId: number; employeeIds?: number[] }[]; frSelectionCount?: number | null }) => {
    if (!roundName.trim()) {
      setError('Please enter a round name');
      throw new Error('Please enter a round name');
    }

    setLoading(true);
    try {
      let evaluatorPairAssignments: Array<{ evaluatorId: number; pairs: Array<{ employee1Id: number; employee2Id: number }> }> | undefined;

      if (isGrouped && evaluatorGroupMappings && evaluatorGroupMappings.length > 0) {
        // Grouped mode: generate within-group pairs (full or partial) per evaluator
        const groupAssignments = generateGroupPairs(evaluatorGroupMappings, comparisonMode, accuracy ?? 70);
        evaluatorPairAssignments = groupAssignments.map(a => ({
          evaluatorId: a.evaluatorId,
          pairs: a.pairs.map(([e1, e2]) => ({ employee1Id: e1, employee2Id: e2 })),
        }));
      } else if (comparisonMode === 'partial' && selectedEvaluators && selectedEvaluators.length > 0 && selectedEmployeeIDs && selectedEmployeeIDs.length > 0) {
        // Standard partial mode: block-overlap algorithm
        const assignments = generatePartialPairAssignments(selectedEmployeeIDs, selectedEvaluators.length, accuracy ?? 70);
        evaluatorPairAssignments = assignments.map((a, i) => ({
          evaluatorId: selectedEvaluators[i].id,
          pairs: a.pairs.map(([e1, e2]) => ({ employee1Id: e1, employee2Id: e2 })),
        }));
      }

      // Create ONE round for all evaluators
      const result: any = await api.createRound({
        roundName,
        startDate: new Date().toISOString(),
        createdBy: userEmail || 'unknown',
        questions,
        selectedEmployeeIDs,
        comparisonMode,
        evaluatorPairAssignments,
        isGrouped,
        finalReviewerId,
        evaluatorGroupMappings,
        hasSecondaryEvaluators,
        secondaryEvaluatorMappings,
        frSelectionCount,
      });

      const newRoundId = result.roundId || result.roundID;
      if (newRoundId && selectedEvaluators && selectedEvaluators.length > 0) {
        await api.assignEvaluators(newRoundId, selectedEvaluators.map(e => e.id), finalReviewerId);
      }

      const [roundsData] = await Promise.all([
        api.getRounds(),
        refreshAssignedRoundIds(),
      ]);
      setRounds(roundsData);
      const active = roundsData.find(r => r.status === 'active');
      if (active) {
        setActiveRound(active);
        setLastWorkingRoundId(active.roundID);
        setShowRankings(false);
        setCurrentComparison(null);
        setUserComparisons([]);
      }
      setShowNewRoundModal(false);
      setActiveTab('rounds');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to create round';
      setError(msg);
      console.error('Error creating round:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleToggleRoundStatus = async (roundId: number, currentStatus: string) => {
    try {
      // Toggle paused → active
      const newStatus = currentStatus === 'active' ? 'paused' : 'active';
      await api.updateRoundStatus(roundId, newStatus);

      const roundsData = await api.getRounds();
      setRounds(roundsData);

      // Update activeRound reference if it changed
      const active = roundsData.find(r => r.status === 'active');
      setActiveRound(active || null);
    } catch (err) {
      setError('Failed to update round status');
      console.error('Error updating round status:', err);
    }
  };

  const handleExportData = async (forceAggregate: boolean = false) => {
    let dataToExport = rankings;
    let exportFileName = 'export';

    if (forceAggregate) {
      try {
        const aggregateRankings = await api.getAggregateRankings();
        dataToExport = aggregateRankings;
        exportFileName = 'Aggregate';
      } catch (err) {
        console.error('Error loading rankings for export:', err);
        alert('No ranking data available to export');
        return;
      }
    } else if (rankingsRoundId) {
      // Export the specific round whose rankings are currently displayed
      const round = rounds.find(r => r.roundID === rankingsRoundId);
      try {
        const evalId = round?.isGrouped ? currentUserId : undefined;
        dataToExport = await api.getRankings(rankingsRoundId, evalId);
        exportFileName = round?.roundName || String(rankingsRoundId);
      } catch (err) {
        console.error('Error loading rankings for export:', err);
        alert('No ranking data available to export');
        return;
      }
    } else if (rankings.length === 0) {
      try {
        const aggregateRankings = await api.getAggregateRankings();
        dataToExport = aggregateRankings;
        exportFileName = 'Aggregate';
      } catch (err) {
        console.error('Error loading rankings for export:', err);
        alert('No ranking data available to export');
        return;
      }
    } else {
      exportFileName = 'Aggregate';
    }

    const csvContent = [
      ['Rank', 'Name', 'Title', 'Department', 'Elo Rating', 'Comparisons', 'Wins', 'Losses'],
      ...dataToExport.map(r => [
        r.rank,
        r.name,
        r.title,
        r.department,
        r.eloRating,
        r.totalComparisons,
        r.wins,
        r.losses
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${exportFileName}-Rankings.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    // Log export event
    api.logDataExport(
      `Exported rankings: ${exportFileName}`,
      'Rankings'
    ).catch(err => console.error('Failed to log export event:', err));
  };

  const refreshRounds = async () => {
    try {
      const roundsData = await api.getRounds();
      setRounds(roundsData);
    } catch (err) {
      console.error('Error refreshing rounds:', err);
    }
  };

  const handleNextPair = async () => {
    // Critical path: next comparison + progress bar data in parallel.
    // loadUnknownEmployees/refreshRounds only affect other tabs — fire-and-forget.
    await Promise.all([
      loadNextPair(),
      loadUserComparisons(),
    ]);
    loadUnknownEmployees(); // background — Skipped tab
    refreshRounds(); // background — Rounds tab
  };


  const handleUndoComparison = async () => {
    const savedId = lastSkippedComparisonId ?? lastSubmittedComparisonId;
    if (!savedId) return;

    if (lastSkippedComparisonId) {
      await api.undoSkip(savedId, currentUserId);
      setLastSkippedComparisonId(null);
    } else {
      await api.undoComparison(savedId, currentUserId);
      setLastSubmittedComparisonId(null);
    }

    // Fetch the undone comparison directly by ID — bypasses the cleanup in the normal
    // loadNextPair flow which deletes comparisons with IsComplete=0 AND AnsweredQuestions=0,
    // which is exactly the state the just-undone comparison is in.
    const undoneComparison = await api.getComparisonById(savedId);
    if (undoneComparison) {
      setCurrentComparison(undoneComparison);
    } else {
      await handleNextPair(); // fallback — already refreshes progress
      return;
    }

    // Refresh progress bar data synchronously, rounds tab in background.
    await Promise.all([
      loadUserComparisons(),
      loadUnknownEmployees(),
    ]);
    refreshRounds(); // background — Rounds tab
  };

  const handleLogin = () => {
    // If the user previously initiated a logout but navigated back before Azure AD
    // completed it, force a fresh credential prompt so they can't silently reuse
    // the still-active Azure AD server-side session.
    const wasLogoutInitiated = sessionStorage.getItem('logoutInitiated') === 'true';
    if (wasLogoutInitiated) {
      sessionStorage.removeItem('logoutInitiated');
      instance.loginRedirect({ ...loginRequest, prompt: 'login' });
    } else {
      instance.loginRedirect(loginRequest);
    }
  };

  const handleLogout = async () => {
    // Await the audit log before redirecting so the request isn't aborted mid-flight
    try {
      await api.logLogout();
    } catch (err) {
      console.error('Failed to log logout event:', err);
    }
    const account = instance.getActiveAccount();
    // Mark that logout was initiated. If the user presses Back on the Azure AD logout
    // page before it completes, this flag tells handleLogin to force prompt:'login'.
    sessionStorage.setItem('logoutInitiated', 'true');
    // Clear local MSAL cache so stale tokens are gone
    await instance.clearCache();
    instance.logoutRedirect({
      // Specifying the account skips Azure AD's "Pick an account" picker
      account: account ?? undefined,
      postLogoutRedirectUri: window.location.origin,
    });
  };

  const userInitials = userName
    ? userName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
    : '?';

  const renderContent = () => {
    // Auth/network error loading the current user (e.g. expired token mid-redirect, transient failure)
    // Show a retry screen rather than the misleading "Account Not Registered" message.
    if (isAuthenticated && userLoadError && !userLoading) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md border border-yellow-100 text-center">
            <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Session Error</h2>
            <p className="text-gray-600 mb-2">
              Signed in as <span className="font-semibold">{userEmail}</span>
            </p>
            <p className="text-gray-500 mb-6">
              Your session could not be verified. This can happen when your login session has expired. Please try again or sign out and sign back in.
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => loadCurrentUser()}
                className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-medium"
              >
                Try Again
              </button>
              <button
                onClick={handleLogout}
                className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all font-medium"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Unregistered user — only shown when getCurrentUser succeeded but user is genuinely not in the DB
    if (isAuthenticated && currentUserId === 0 && !userLoadError && !loading && !userLoading) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md border border-yellow-100 text-center">
            <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Account Not Registered</h2>
            <p className="text-gray-600 mb-2">
              Signed in as <span className="font-semibold">{userEmail}</span>
            </p>
            <p className="text-gray-500 mb-6">
              Your account is not yet registered in the system. Please contact an administrator or sync users from EntraID.
            </p>
            <button
              onClick={handleLogout}
              className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all font-medium"
            >
              Sign Out
            </button>
          </div>
        </div>
      );
    }

    // Error display
    if (error && !currentComparison) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md border border-red-100">
            <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-3">Connection Error</h2>
            <p className="text-gray-600 text-center mb-6">{error}</p>
            <button
              onClick={() => {
                setError(null);
                loadInitialData();
              }}
              className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-bold shadow-lg hover:shadow-xl"
            >
              Retry Connection
            </button>
          </div>
        </div>
      );
    }

    // Loading state
    if (loading) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-20 w-20 border-b-4 border-blue-600 mx-auto mb-6 shadow-lg"></div>
            <p className="text-gray-700 text-lg font-medium">Loading data from database...</p>
          </div>
        </div>
      );
    }

    const roundParticipantCount = activeRound?.participantCount ?? 0;
    const myUnknownCount = unknownEmployees.filter(e => e.markedByUserId === currentUserId).length;
    const eligibleEmployeeCount = roundParticipantCount - myUnknownCount;
    // For partial and grouped mode, comparisons are pre-seeded per evaluator —
    // use the exact evaluator-specific count rather than the full round-robin formula.
    const totalPossibleComparisons =
      (activeRound?.comparisonMode === 'partial' || activeRound?.isGrouped) && currentComparison?.evaluatorTotalPairs
        ? currentComparison.evaluatorTotalPairs
        : (eligibleEmployeeCount * (eligibleEmployeeCount - 1)) / 2;

    // Comparison View - Loading next pair
    if (currentView === 'comparison' && loadingComparison && !showRankings) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-20 w-20 border-b-4 border-blue-600 mx-auto mb-6 shadow-lg"></div>
            <p className="text-gray-700 text-lg font-medium">Loading next comparison...</p>
          </div>
        </div>
      );
    }

    // Comparison View (Rankings Complete)
    if (currentView === 'comparison' && showRankings) {
      return (
        <RankingsView
          rankings={rankings}
          activeRound={rankingsRoundId ? (rounds.find(r => r.roundID === rankingsRoundId) ?? activeRound) : activeRound}
          userComparisons={userComparisons}
          unknownEmployeeCount={myUnknownCount}
          userRole={userRole}
          hasMorePairs={currentComparison !== null}
          userName={userName}
          userEmail={userEmail}
          userPhotoURL={currentUser?.photoURL}
          onLogout={handleLogout}
          onContinueComparisons={() => {
            setShowRankings(false);
            setCurrentView('dashboard');
            setActiveTab('overview');
          }}
          onBackToComparisons={() => {
            setShowRankings(false);
            loadNextPair();
          }}
          onExportData={() => handleExportData()}
          onNavigate={(tab) => { setCurrentView('dashboard'); setActiveTab(tab); }}
        />
      );
    }

    // Comparison View (Active Comparison)
    if (currentView === 'comparison' && currentComparison) {
      return (
        <ComparisonView
          currentComparison={currentComparison}
          activeRound={activeRound}
          userComparisons={userComparisons}
          totalPossibleComparisons={totalPossibleComparisons}
          unknownEmployeeCount={myUnknownCount}
          userRole={userRole}
          lastSubmittedComparisonId={lastSkippedComparisonId ?? lastSubmittedComparisonId}
          userName={userName}
          userEmail={userEmail}
          userPhotoURL={currentUser?.photoURL}
          onLogout={handleLogout}
          onViewRankings={async () => {
            if (activeRound) {
              await loadRankings(activeRound.roundID);
            }
            setShowRankings(true);
          }}
          onNavigate={(tab) => { setCurrentView('dashboard'); setActiveTab(tab); }}
          onNextPair={handleNextPair}
          onComparisonSubmitted={(id) => { setLastSubmittedComparisonId(id); setLastSkippedComparisonId(null); }}
          onSkip={(id) => { setLastSkippedComparisonId(id); setLastSubmittedComparisonId(null); }}
          onUndo={handleUndoComparison}
        />
      );
    }

    // Admin Dashboard View — flex column layout: fixed header + scrollable content
    return (
      <div className="h-screen flex flex-col bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden">
        {/* Header + Navigation — fixed at top, never scrolls */}
        {(() => {
          const tabs = [
            { id: 'overview' as TabType, label: 'Overview', icon: BarChart3 },
            { id: 'rounds' as TabType, label: 'Evaluation Rounds', icon: PlayCircle },
            { id: 'rankings' as TabType, label: 'Rankings', icon: TrendingUp },
            { id: 'skipped' as TabType, label: 'Skipped', icon: UserX },
            ...(userRole === 'admin' ? [
              { id: 'employees' as TabType, label: 'Employees', icon: Users },
              { id: 'settings' as TabType, label: 'Settings', icon: Settings },
              { id: 'auditlog' as TabType, label: 'Audit Log', icon: FileText }
            ] : []),
          ];
          const activeTabObj = tabs.find(t => t.id === activeTab) || tabs[0];

          const profileSection = (
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="text-right hidden md:block">
                <p className="text-sm font-semibold text-gray-900 truncate max-w-[150px]">{userName}</p>
                <p className="text-xs text-gray-500 truncate max-w-[150px]">{userEmail}</p>
              </div>
              {resolvePhotoUrl(currentUser?.photoURL) ? (
                <img
                  src={resolvePhotoUrl(currentUser?.photoURL)!}
                  alt={userName}
                  className="w-9 h-9 sm:w-12 sm:h-12 rounded-full object-cover shadow-lg flex-shrink-0"
                  onError={e => { (e.target as HTMLImageElement).style.display = 'none'; (e.target as HTMLImageElement).nextElementSibling?.removeAttribute('style'); }}
                />
              ) : null}
              <div
                className="w-9 h-9 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center text-white font-bold shadow-lg flex-shrink-0 text-sm sm:text-base"
                style={resolvePhotoUrl(currentUser?.photoURL) ? { display: 'none' } : undefined}
              >
                {userInitials}
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-1 px-2 py-2 sm:px-3 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors font-medium min-h-[44px] min-w-[44px] justify-center"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Sign Out</span>
              </button>
            </div>
          );

          return (
            <div className="flex-shrink-0">
              {/* Desktop header: logo + actions + profile */}
              <header className="bg-white border-b-2 border-gray-200 shadow-lg">
                <div className="max-w-7xl mx-auto px-3 py-3 sm:px-6 sm:py-5">
                  {/* Mobile: single row — logo + "Go to Comparisons" + profile + logout */}
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                      <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center shadow-lg flex-shrink-0">
                        <BarChart3 className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                      </div>
                      <h1 className="text-xl sm:text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent truncate">
                        Pairwise Comparison Tool
                      </h1>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
                      {activeRound && activeRound.status === 'active' && assignedRoundIds?.includes(activeRound.roundID) && (
                        <button
                          onClick={() => {
                            setShowRankings(false);
                            setCurrentView('comparison');
                            loadNextPair();
                          }}
                          className="hidden xs:flex px-3 py-2 sm:px-5 sm:py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-md hover:shadow-lg font-medium text-sm sm:text-base min-h-[44px] items-center"
                        >
                          <PlayCircle className="w-4 h-4 sm:w-5 sm:h-5 inline mr-1 sm:mr-2" />
                          <span className="hidden sm:inline">Go to </span>Comparisons
                        </button>
                      )}
                      {/* Profile: hidden on mobile (shown in nav bar below) */}
                      <div className="hidden md:flex">{profileSection}</div>
                    </div>
                  </div>
                </div>
              </header>

              {/* Navigation bar */}
              <div className="bg-white border-b border-gray-200 shadow-sm">
                <div className="max-w-7xl mx-auto px-3 sm:px-6">
                  {/* Mobile: single row — hamburger + active tab | profile + logout */}
                  <div className="md:hidden flex items-center justify-between py-2">
                    <button
                      onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                      className="flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-gray-100 transition-colors min-h-[44px]"
                    >
                      {mobileMenuOpen ? <X className="w-5 h-5 text-gray-600" /> : <Menu className="w-5 h-5 text-gray-600" />}
                      <activeTabObj.icon className="w-4 h-4 text-blue-600" />
                      <span className="font-semibold text-sm text-gray-900">{activeTabObj.label}</span>
                    </button>
                    {profileSection}
                  </div>
                  {/* Mobile: dropdown menu */}
                  {mobileMenuOpen && (
                    <div className="md:hidden pb-2 border-t border-gray-100">
                      <nav className="flex flex-col gap-1 pt-2">
                        {activeRound && activeRound.status === 'active' && assignedRoundIds?.includes(activeRound.roundID) && (
                          <button
                            onClick={() => { setShowRankings(false); setCurrentView('comparison'); loadNextPair(); setMobileMenuOpen(false); }}
                            className="flex items-center gap-3 px-3 py-3 rounded-lg transition-all min-h-[44px] text-white bg-gradient-to-r from-blue-600 to-purple-600 font-semibold text-sm"
                          >
                            <PlayCircle className="w-5 h-5" />
                            Go to Comparisons
                          </button>
                        )}
                        {tabs.map(tab => (
                          <button
                            key={tab.id}
                            onClick={() => { setActiveTab(tab.id); setMobileMenuOpen(false); }}
                            className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-all min-h-[44px] ${activeTab === tab.id
                              ? 'bg-blue-50 text-blue-600 font-semibold'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                          >
                            <tab.icon className="w-5 h-5" />
                            <span className="font-medium text-sm">{tab.label}</span>
                          </button>
                        ))}
                      </nav>
                    </div>
                  )}
                  {/* Desktop: horizontal tabs */}
                  <nav className="hidden md:flex gap-4 lg:gap-8 -mb-px">
                    {tabs.map(tab => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 px-1 py-4 border-b-2 transition-all whitespace-nowrap min-h-[44px] ${activeTab === tab.id
                          ? 'border-blue-600 text-blue-600 font-semibold'
                          : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                        }`}
                      >
                        <tab.icon className="w-5 h-5" />
                        <span className="font-medium text-sm lg:text-base">{tab.label}</span>
                      </button>
                    ))}
                  </nav>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Scrollable content area — fills remaining height */}
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-3 py-4 sm:px-6 sm:py-8">
          {activeTab === 'overview' && (
            <OverviewTab
              rounds={visibleRounds}
              activeRound={activeRound}
              userRole={userRole}
              onNewRound={() => setShowNewRoundModal(true)}
              onExportData={() => handleExportData(true)}
            />
          )}

          {activeTab === 'rounds' && (
            <RoundsTab
              rounds={visibleRounds}
              userRole={userRole}
              assignedRoundIds={assignedRoundIds}
              currentUserEmail={userEmail}
              onNewRound={() => setShowNewRoundModal(true)}
              onToggleRoundStatus={handleToggleRoundStatus}
              onDeleteRound={async (roundId) => {
                try {
                  await api.deleteRound(roundId);
                  const roundsData = await api.getRounds();
                  setRounds(roundsData);
                } catch (err: any) {
                  setError(err.message || 'Failed to delete round');
                  console.error('Error deleting round:', err);
                }
              }}
              onStartComparisons={(roundId) => {
                const round = rounds.find(r => r.roundID === roundId);
                if (round) {
                  setActiveRound(round);
                  setLastWorkingRoundId(round.roundID);
                  setShowRankings(false);
                  setCurrentView('comparison');
                }
              }}
            />
          )}

          {activeTab === 'rankings' && (
            <RankingsTab
              key={activeTab}
              rounds={myRounds}
              userRole={userRole}
              currentUserId={currentUserId}
            />
          )}

          {activeTab === 'skipped' && (
            <SkippedComparisonsTab
              key={activeTab}
              rounds={myRounds}
              currentRoundId={lastWorkingRoundId}
            />
          )}

          <div style={{ display: activeTab === 'employees' ? 'block' : 'none' }}>
            <EmployeesTab currentUserId={currentUserId} />
          </div>

            {activeTab === 'settings' && (
              <SettingsTab
                eloKFactor={eloKFactor}
                setEloKFactor={setEloKFactor}
                initialRating={initialRating}
                setInitialRating={setInitialRating}
                rounds={rounds}
                userComparisons={userComparisons}
                userRole={userRole}
              />
            )}

            {activeTab === 'auditlog' && userRole === 'admin' && (
              <AuditLogTab key={activeTab} />
            )}
          </div>
        </main>

        <NewRoundModal
          isOpen={showNewRoundModal}
          onClose={() => setShowNewRoundModal(false)}
          onCreate={handleCreateRound}
          loading={loading}
          currentUserId={currentUserId}
        />
      </div>
    );
  };

  return (
    <>
      <UnauthenticatedTemplate>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md text-center border border-gray-100">
            <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center mx-auto mb-6 shadow-lg">
              <BarChart3 className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-3">
              Pairwise Comparison Tool
            </h1>
            <p className="text-gray-600 mb-8">Sign in with your organization account to continue</p>
            <button
              onClick={handleLogin}
              className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-bold shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
            >
              <LogIn className="w-5 h-5" />
              Sign in
            </button>
          </div>
        </div>
      </UnauthenticatedTemplate>

      <AuthenticatedTemplate>
        {renderContent()}
      </AuthenticatedTemplate>

      {/* Notification toast */}
      {notification && (
        <div className={`fixed bottom-6 right-6 z-50 max-w-sm rounded-xl shadow-lg p-4 flex items-start gap-3 ${
          notification.type === 'error' ? 'bg-red-50 border border-red-200' :
          notification.type === 'warning' ? 'bg-amber-50 border border-amber-200' :
          'bg-blue-50 border border-blue-200'
        }`}>
          <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
            notification.type === 'error' ? 'text-red-500' :
            notification.type === 'warning' ? 'text-amber-500' :
            'text-blue-500'
          }`} />
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-semibold ${
              notification.type === 'error' ? 'text-red-800' :
              notification.type === 'warning' ? 'text-amber-800' :
              'text-blue-800'
            }`}>{notification.type === 'error' ? 'Error' : notification.type === 'warning' ? 'Notice' : 'Info'}</p>
            <p className={`text-sm mt-0.5 ${
              notification.type === 'error' ? 'text-red-600' :
              notification.type === 'warning' ? 'text-amber-600' :
              'text-blue-600'
            }`}>{notification.message}</p>
          </div>
          <button onClick={() => setNotification(null)} className={`p-1 rounded-lg transition-colors ${
            notification.type === 'error' ? 'hover:bg-red-100' :
            notification.type === 'warning' ? 'hover:bg-amber-100' :
            'hover:bg-blue-100'
          }`}>
            <X className={`w-4 h-4 ${
              notification.type === 'error' ? 'text-red-400' :
              notification.type === 'warning' ? 'text-amber-400' :
              'text-blue-400'
            }`} />
          </button>
        </div>
      )}
    </>
  );
};

export default App;
